import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer'
import { Button } from '../ui/button'
import { ShoppingCart, X } from 'lucide-react'
import { useMediaQuery } from '@/hooks/use-media-query'
import ActionButton from './ActionButton'
import { CommonModalType } from '@/utils/interfaces'
import { dispatch, useSelector } from '@/lib/store'
import { askUserToRemoveItems } from '@/lib/reducers/cartReducer'
import { updatePackageData } from '@/lib/reducers/packageReducer'
import { deepClone, isEmpty } from '@/utils/commonFunc'
import { consumerRequest } from '@/helper/request'
import { PRODUCT_CHECK_DUPLICATES_API } from '@/helper/apiConstants'
import { useRouter } from 'next/navigation'
import { updateOrderData } from '@/lib/reducers/orderReducer'
import { CHECKOUT_PAGE, CONSUMER_WEB_PLATFORM, CURRENT_SUBSIDIARY } from '@/utils/constants'
import TotalProductsInCart from '../Cart/TotalProductsInCart'
import { loadState } from '@/utils/localStorage'
import { eventConstants, trackEvent } from '@/utils/events'

const CartDrawer = ({ open, onOpenChange }: CommonModalType) => {
  const router = useRouter()

  const cartState: any = useSelector((state) => state.cart)
  const cityState = useSelector((state) => state.city)
  const packageState = useSelector((state) => state.package)
  const orderState: any = useSelector((state) => state.order)
  const loggedInMobileNumber = loadState('ctxVal') || null

  const { value: isDesktop } = useMediaQuery('(min-width: 768px)')

  const CART_ITEMS = cartState?.cartDtoList || []

  const handleAskUserToRemoveProductFromCart = (item: any) => {
    dispatch(
      askUserToRemoveItems({ ...cartState, itemToRemoveFromCart: { ...item } })
    )
  }

  const handleProceedToCheckout = async () => {
    if (CART_ITEMS?.[0]?.productDtoList?.length > 1) {
      handleCheckDuplicateProductsInCart()
    } else {
      const eventPayload = {
        u_mb: loggedInMobileNumber,
        subsidiary: CURRENT_SUBSIDIARY,
        city: cityState?.currentCityObj?.name || null,
        product_list: !isEmpty(CART_ITEMS?.[0]?.productDtoList)
          ? CART_ITEMS?.[0]?.productDtoList
              ?.map((item: any) => `${item?.name}`)
              ?.join(', ')
          : [],
        amount: cartState?.totalAmount,
        app: CONSUMER_WEB_PLATFORM,
      }
      trackEvent(eventConstants.BEGIN_CHECKOUT, eventPayload)
      router.push(CHECKOUT_PAGE)

      onOpenChange(false)
    }
  }

  const handleCheckDuplicateProductsInCart = async () => {
    const formData = {
      patientProductDetailRequestDtoList: [
        {
          cityUuid: cityState?.currentCityObj?.uuid,
          cartUuid: CART_ITEMS?.[0]?.uuid,
          patientCode: cartState?.activePatientDetailDto?.code,
          patientName: cartState?.activePatientDetailDto?.firstName,
          productDetailDtoList:
            CART_ITEMS &&
            CART_ITEMS?.[0]?.productDtoList?.length > 0 &&
            CART_ITEMS?.[0]?.productDtoList?.map((item: any) => {
              return item
            }),
        },
      ],
    }
    const apiResponse: any = await consumerRequest(
      PRODUCT_CHECK_DUPLICATES_API,
      'post',
      formData
    )
    if (apiResponse?.data[0]?.duplicateProduct) {
      dispatch(
        updatePackageData({
          ...packageState,
          duplicateProductModalIsOpen: true,
          duplicateProductData: apiResponse?.data[0],
        })
      )
    } else {
      const updatedOrderReducer = deepClone(orderState)
      updatedOrderReducer.cartDtoList = cartState.cartDtoList
      updatedOrderReducer.paymentDetailDto.testCharges = cartState.totalAmount
      updatedOrderReducer.paymentDetailDto.totalAmount = cartState.totalAmount
      dispatch(updateOrderData(updatedOrderReducer))

      const eventPayload = {
        u_mb: loggedInMobileNumber,
        subsidiary: CURRENT_SUBSIDIARY,
        city: cityState?.currentCityObj?.name || null,
        product_list: !isEmpty(CART_ITEMS?.[0]?.productDtoList)
          ? CART_ITEMS?.[0]?.productDtoList
              ?.map((item: any) => `${item?.code}:${item?.name}`)
              ?.join(', ')
          : [],
        amount: cartState?.totalAmount,
        app: CONSUMER_WEB_PLATFORM,
      }
      trackEvent(eventConstants.BEGIN_CHECKOUT, eventPayload)
      router.push(CHECKOUT_PAGE)
      onOpenChange(false)
    }
  }

  const collectiveCartItems = CART_ITEMS?.reduce((total: any, item: any) => {
    return total + item?.productDtoList?.length
  }, 0)

  return (
    <>
      <Drawer
        direction={isDesktop ? 'right' : 'bottom'}
        open={open}
        // onClose={onOpenChange}
        onOpenChange={onOpenChange}
      >
        <DrawerContent className="mt-0 sm:rounded-md md:rounded-xl lg:rounded-xl xl:rounded-xl   md:inset-2 w-full  md:w-[480px] lg:w-[480px] ml-auto custom_drawer">
          <div className="relative h-full overflow-hidden">
            <DrawerHeader className="p-5 flex items-center justify-between shadow-elevation3">
              <div className="text-left">
                <DrawerTitle className="text-body-lg md:text-heading-md font-semibold">
                  Your Cart
                </DrawerTitle>
                <DrawerDescription
                  className={`text-label-sm font-semibold ${
                    CART_ITEMS?.length > 0
                      ? 'text-primary-500'
                      : 'text-gray-700'
                  }`}
                >
                  {CART_ITEMS?.length > 0
                    ? `${collectiveCartItems} Tests added`
                    : `No tests`}
                  {CART_ITEMS?.length > 0 && (
                    <>
                      {' '}
                      |{' '}
                      <Button
                        className="bg-transparent p-0 m-0 cursor-pointer h-auto text-label-sm text-gray-700 shadow-none"
                        onClick={() =>
                          handleAskUserToRemoveProductFromCart(
                            CART_ITEMS?.[0]?.uuid
                          )
                        }
                      >
                        Clear cart
                      </Button>
                    </>
                  )}
                </DrawerDescription>
              </div>
              <DrawerClose asChild>
                <Button className="bg-gray-100 p-0 m-0 cursor-pointer h-auto shadow-none">
                  <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                    <X size={18} className="text-gray-800" />
                  </div>
                </Button>
              </DrawerClose>
            </DrawerHeader>

            <div className="flex flex-col justify-between gap-4 h-full">
              {/* <div className="px-2 py-6 sm:px-4 overflow-y-auto"> */}
              <div className="px-2 py-4">
                <TotalProductsInCart />
              </div>
              {/* </div> */}
              {CART_ITEMS?.length > 0 && (
                <DrawerFooter className="px-6 py-4 sticky bottom-0 left-0 right-0 border-t border-solid border-gray-100 bg-white-100 rounded-xl">
                  <div className="flex gap-6 justify-between items-center">
                    <div className="flex gap-6 items-center ">
                      <Button className="bg-transparent p-0 m-0 shadow-none ">
                        <ShoppingCart size={22} className="text-gray-500" />
                      </Button>
                      {!!cartState?.totalAmount && (
                        <p className="text-body-lg font-bold ">
                          ₹{cartState?.totalAmount}
                        </p>
                      )}
                    </div>
                    <ActionButton
                      variant="primary"
                      title="Proceed to checkout"
                      className="w-full"
                      onClick={handleProceedToCheckout}
                    />
                  </div>
                </DrawerFooter>
              )}
            </div>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  )
}

export default CartDrawer
